/**
 * Picking up is not joining the meeting. The subscription hears the former
 * before a credential or camera is ready, so the app remembers it for the
 * waiting caption while the package owns presence and expansion.
 */
import { callStore } from "@repo/conferencing/components";
import { createStore } from "@xstate/store";
import { useSelector } from "@xstate/store-react";
import { callRoomKey } from "./call-room";
import { stopOutgoingRingback } from "./call-sounds";

export const callAnswerStore = createStore({
  context: {
    answeredCallId: undefined as string | undefined,
    outgoingCallId: undefined as string | undefined,
  },
  on: {
    outgoing: (ctx, event: { callId: string }) => ({ ...ctx, outgoingCallId: event.callId }),
    answered: (ctx, event: { callId: string }) => ({ ...ctx, answeredCallId: event.callId }),
    reset: () => ({ answeredCallId: undefined, outgoingCallId: undefined }),
  },
});

// The provider closes the room BEFORE onLeave. Keep the answer through that
// callback, or leaving a connected call would try to abandon it. A new entry
// clears it, including reopening the same room after leaving.
let previousRoomKey = callStore.get().context.active?.key;
callStore.subscribe((snapshot) => {
  const roomKey = snapshot.context.active?.key;
  if (roomKey && roomKey !== previousRoomKey) {
    callAnswerStore.send({ type: "reset" });
  }
  previousRoomKey = roomKey;
});

export function markCallAnswered(callId: string): void {
  // Category-wide notifications can arrive after this caller has moved on.
  if (callStore.get().context.active?.key !== callRoomKey(callId)) return;
  callAnswerStore.send({ type: "answered", callId });
  // FHIR pickup precedes meeting presence; a video tile can arrive much later.
  stopOutgoingRingback(callId);
}

export const useCallAnswered = (): boolean =>
  useSelector(callAnswerStore, (s) => s.context.answeredCallId !== undefined);
